import { useEffect, useState } from "react";
import axios from "axios";
import { BarChart3, PieChart, TrendingUp, DollarSign, Package, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from "recharts";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#18181B', '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

export default function Reports() {
  const [inventoryValue, setInventoryValue] = useState(null);
  const [stockMovement, setStockMovement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30");

  useEffect(() => {
    fetchReports();
  }, [period]);

  const fetchReports = async () => {
    try {
      const [valueRes, movementRes] = await Promise.all([
        axios.get(`${API}/reports/inventory-value`),
        axios.get(`${API}/reports/stock-movement?days=${period}`)
      ]);
      setInventoryValue(valueRes.data);
      setStockMovement(movementRes.data);
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Failed to fetch reports");
    } finally {
      setLoading(false);
    }
  };

  const handleExportReport = async (fmt) => {
    try {
      const res = await axios.get(`${API}/export/expenses-report?format=${fmt}`, { responseType: "blob" });
      const ext = fmt === "pdf" ? "pdf" : "xlsx";
      const mime = fmt === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const url = window.URL.createObjectURL(new Blob([res.data], { type: mime }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `expenses_report.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground">Loading reports...</div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page">
      <div className="page-header flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">Analytics and inventory insights</p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="rounded-none uppercase text-xs tracking-wider">
                <Download className="w-4 h-4 mr-2" />
                Export Report
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExportReport("pdf")}>PDF Report (.pdf)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportReport("xlsx")}>Excel (.xlsx)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-[180px] rounded-none" data-testid="period-select">
            <SelectValue placeholder="Select period" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="365">Last year</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      <div className="page-content space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="rounded-none border-zinc-200" data-testid="total-value-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total Inventory Value</p>
                  <p className="text-2xl font-semibold font-mono mt-1">{formatCurrency(inventoryValue?.total_value)}</p>
                </div>
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-zinc-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none border-zinc-200" data-testid="stock-in-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Stock In ({period}d)</p>
                  <p className="text-2xl font-semibold font-mono mt-1 text-green-600">+{stockMovement?.summary?.total_stock_in || 0}</p>
                </div>
                <div className="w-12 h-12 bg-green-50 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none border-zinc-200" data-testid="stock-out-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Stock Out ({period}d)</p>
                  <p className="text-2xl font-semibold font-mono mt-1 text-red-600">-{stockMovement?.summary?.total_stock_out || 0}</p>
                </div>
                <div className="w-12 h-12 bg-red-50 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-red-600 rotate-180" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-none border-zinc-200" data-testid="categories-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Categories</p>
                  <p className="text-2xl font-semibold font-mono mt-1">{inventoryValue?.by_category?.length || 0}</p>
                </div>
                <div className="w-12 h-12 bg-zinc-100 flex items-center justify-center">
                  <Package className="w-6 h-6 text-zinc-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Inventory Value by Category */}
          <Card className="rounded-none border-zinc-200" data-testid="value-by-category-chart">
            <CardHeader className="border-b border-zinc-100 bg-zinc-50/50 py-4">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
                <PieChart className="w-4 h-4" />
                Inventory Value by Category
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {inventoryValue?.by_category?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <RechartsPie>
                    <Pie
                      data={inventoryValue.by_category}
                      dataKey="value"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      label={({ category, percent }) => `${category} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {inventoryValue.by_category.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      formatter={(value) => formatCurrency(value)}
                      contentStyle={{ borderRadius: 0, border: '1px solid #e4e4e7' }}
                    />
                  </RechartsPie>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No category data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Stock Movement Chart */}
          <Card className="rounded-none border-zinc-200" data-testid="stock-movement-chart">
            <CardHeader className="border-b border-zinc-100 bg-zinc-50/50 py-4">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider flex items-center gap-2">
                <BarChart3 className="w-4 h-4" />
                Stock Movement (Last {period} Days)
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {stockMovement?.movement?.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={stockMovement.movement}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }}
                      tickFormatter={(value) => {
                        const date = new Date(value);
                        return `${date.getMonth() + 1}/${date.getDate()}`;
                      }}
                    />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip 
                      contentStyle={{ borderRadius: 0, border: '1px solid #e4e4e7' }}
                      labelFormatter={(value) => new Date(value).toLocaleDateString()}
                    />
                    <Bar dataKey="stock_in" fill="#10B981" name="Stock In" />
                    <Bar dataKey="stock_out" fill="#EF4444" name="Stock Out" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  No movement data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Category Breakdown Table */}
        <Card className="rounded-none border-zinc-200" data-testid="category-breakdown">
          <CardHeader className="border-b border-zinc-100 bg-zinc-50/50 py-4">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider">
              Category Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {inventoryValue?.by_category?.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th className="text-right">Components</th>
                      <th className="text-right">Total Quantity</th>
                      <th className="text-right">Total Value</th>
                      <th className="text-right">% of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inventoryValue.by_category.map((cat, idx) => (
                      <tr key={cat.category} data-testid={`category-row-${idx}`}>
                        <td>
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3" 
                              style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                            />
                            <span className="font-medium font-sans">{cat.category}</span>
                          </div>
                        </td>
                        <td className="text-right font-mono">{cat.count}</td>
                        <td className="text-right font-mono">{cat.quantity}</td>
                        <td className="text-right font-mono font-semibold">{formatCurrency(cat.value)}</td>
                        <td className="text-right">
                          <Badge variant="outline" className="rounded-none font-mono">
                            {((cat.value / inventoryValue.total_value) * 100).toFixed(1)}%
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-zinc-50">
                      <td className="font-semibold uppercase text-xs tracking-wider">Total</td>
                      <td className="text-right font-mono font-semibold">
                        {inventoryValue.by_category.reduce((sum, c) => sum + c.count, 0)}
                      </td>
                      <td className="text-right font-mono font-semibold">
                        {inventoryValue.by_category.reduce((sum, c) => sum + c.quantity, 0)}
                      </td>
                      <td className="text-right font-mono font-bold">
                        {formatCurrency(inventoryValue.total_value)}
                      </td>
                      <td className="text-right">
                        <Badge className="rounded-none font-mono">100%</Badge>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                No category data available. Add components with categories to see breakdown.
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
